import * as XLSX from 'xlsx';

export interface ExcelExportOptions {
  filename: string;
  sheetName?: string;
  headers: string[];
  data: any[][];
  additionalInfo?: { label: string; value: string | number }[];
}

/**
 * Универсальная функция для экспорта данных в Excel
 */
export interface ExcelMultiSheetOptions {
  filename: string;
  sheets: { name: string; rows: any[][] }[];
}

/** Несколько листов в одной книге (например: «Сводка» + детализация). */
export const exportToExcelMultiSheet = (options: ExcelMultiSheetOptions): void => {
  try {
    const { filename, sheets } = options;
    const workbook = XLSX.utils.book_new();

    for (const sheet of sheets) {
      const safeName = sheet.name.replace(/[[\]:*?/\\]/g, '_').substring(0, 31) || 'Sheet';
      const worksheet = XLSX.utils.aoa_to_sheet(sheet.rows);
      XLSX.utils.book_append_sheet(workbook, worksheet, safeName);
    }

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Ошибка экспорта в Excel:', error);
    alert('Произошла ошибка при экспорте в Excel');
  }
};

export const exportToExcel = (options: ExcelExportOptions): void => {
  try {
    const { filename, sheetName = 'Данные', headers, data, additionalInfo } = options;

    // Создаем рабочую книгу
    const workbook = XLSX.utils.book_new();

    // Подготавливаем данные для листа
    const worksheetData: any[][] = [];

    // Добавляем заголовки
    worksheetData.push(headers);

    // Добавляем основные данные
    data.forEach(row => {
      worksheetData.push(row);
    });

    // Добавляем дополнительную информацию (если есть)
    if (additionalInfo && additionalInfo.length > 0) {
      worksheetData.push([]); // Пустая строка
      additionalInfo.forEach(info => {
        worksheetData.push([info.label, info.value]);
      });
    }

    // Создаем лист из данных
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Настраиваем ширину колонок
    const columnWidths = headers.map((_, index) => {
      let maxLength = headers[index].length;
      
      // Находим максимальную длину в колонке
      worksheetData.forEach(row => {
        if (row[index] && row[index].toString().length > maxLength) {
          maxLength = row[index].toString().length;
        }
      });
      
      return { wch: Math.min(Math.max(maxLength + 2, 10), 50) };
    });

    worksheet['!cols'] = columnWidths;

    // Добавляем лист в рабочую книгу
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Генерируем файл и скачиваем
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    console.log(`Excel файл "${filename}.xlsx" успешно экспортирован`);
  } catch (error) {
    console.error('Ошибка экспорта в Excel:', error);
    alert('Произошла ошибка при экспорте в Excel');
  }
};

/**
 * Форматирует дату для использования в Excel
 */
export const formatDateForExcel = (date: Date): string => {
  return date.toLocaleDateString('ru-RU');
};

/**
 * Форматирует дату и время для использования в Excel
 */
export const formatDateTimeForExcel = (date: Date): string => {
  return date.toLocaleString('ru-RU');
};
